import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { formatQuoteMoney } from '../../utils/quotePricing';

interface MonthRow {
  key: string;
  label: string;
  enquiries: number;
  quotesIssued: number;
  quotesIssuedMinor: number;
  ordersWon: number;
  ordersWonMinor: number;
  ordersDeclined: number;
  conversionRate: number;
  averageOrderMinor: number;
  receivedMinor: number;
  depositsMinor: number;
  balancesMinor: number;
  refundsMinor: number;
  netReceivedMinor: number;
  outstandingMinor: number;
  overdueMinor: number;
  shootsConfirmed: number;
  shootsDone: number;
  shootsDelivered: number;
  averageTurnaroundDays: number;
  byShootType: Record<string, { orders: number; valueMinor: number }>;
}

interface FinanceResponse {
  year: number;
  years: number[];
  thisMonth: string;
  months: MonthRow[];
  total: MonthRow;
  previousYear: { year: number; total: MonthRow };
  openOutstandingMinor: number;
}

const rand = (minor?: number) => formatQuoteMoney(minor || 0, 'R');

function MonthBar({ value, max, tone }: { value: number; max: number; tone: 'won' | 'cash' }) {
  const width = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <span className={`studio-finance-bar is-${tone}`} aria-hidden="true">
      <span style={{ width: `${width}%` }} />
    </span>
  );
}

function delta(current: number, previous: number) {
  if (!previous) return current ? 'new' : '';
  const change = Math.round(((current - previous) / previous) * 100);
  if (change === 0) return 'level';
  return `${change > 0 ? '+' : ''}${change}% on last year`;
}

const StudioFinance = ({
  onOpenPayments,
  onOpenClients,
}: {
  onOpenPayments?: (focus?: string) => void;
  onOpenClients?: (stage?: string) => void;
}) => {
  const [data, setData] = useState<FinanceResponse | null>(null);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [openMonth, setOpenMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const query = year ? `?year=${year}` : '';
        const response = await fetch(`${API_BASE_URL}/studio/finance/monthly${query}`);
        if (!response.ok) throw new Error('The finance figures could not be loaded.');
        const body = await response.json();
        if (!active) return;
        setData(body);
        if (!openMonth && body.thisMonth && String(body.thisMonth).startsWith(String(body.year))) setOpenMonth(body.thisMonth);
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : 'The finance figures could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // openMonth is set once from the response; it must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const months = data?.months || [];
  const shownMonths = useMemo(() => months.filter((row) => (
    row.enquiries
    || row.quotesIssued
    || row.ordersWon
    || row.ordersDeclined
    || row.receivedMinor
    || row.refundsMinor
    || row.shootsConfirmed
    || row.shootsDone
    || row.shootsDelivered
  )), [months]);
  const maxWon = useMemo(() => Math.max(0, ...shownMonths.map((row) => row.ordersWonMinor)), [shownMonths]);
  const maxCash = useMemo(() => Math.max(0, ...shownMonths.map((row) => row.netReceivedMinor)), [shownMonths]);
  const current = shownMonths.find((row) => row.key === openMonth)
    || shownMonths.find((row) => row.key === data?.thisMonth)
    || shownMonths[shownMonths.length - 1]
    || null;
  const total = data?.total;
  const previous = data?.previousYear.total;
  const shootTypes = useMemo(() => {
    const source = current && current.ordersWon ? current.byShootType : (total ? total.byShootType : {});
    return Object.entries(source || {})
      .map(([type, row]) => ({ type, ...row }))
      .sort((left, right) => right.valueMinor - left.valueMinor);
  }, [current, total]);

  return (
    <div className="studio-home studio-finance" data-testid="studio-finance">
      <header className="studio-home-head">
        <div>
          <h2>Finance</h2>
          <p>Month by month: what came in, what was booked, what is still owed, and how many shoots. Deleted and test quotations are left out.</p>
        </div>
        <div className="studio-home-actions">
          {data && data.years.length > 1 ? (
            <select aria-label="Year" value={data.year} onChange={(event) => { setYear(Number(event.target.value)); setOpenMonth(''); }}>
              {data.years.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          ) : null}
          {onOpenPayments ? <button type="button" className="studio-secondary" onClick={() => onOpenPayments('outstanding')}>Payments</button> : null}
        </div>
      </header>
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {loading && !data ? <p className="studio-note">Adding up the year…</p> : null}
      {data && total ? (
        <>
          <section aria-label={`${data.year} so far`}>
            <h3 className="studio-section-label">{data.year} so far</h3>
            <div className="studio-money studio-finance-totals" data-testid="finance-year-totals">
              <button type="button" onClick={() => onOpenPayments?.('received')}>
                <small>Cash in</small>
                <strong>{rand(total.netReceivedMinor)}</strong>
                <span className="studio-glance-hint">{previous ? delta(total.netReceivedMinor, previous.netReceivedMinor) : ''}</span>
              </button>
              <button type="button" onClick={() => onOpenClients?.('booked')}>
                <small>Orders won</small>
                <strong>{total.ordersWon}</strong>
                <span className="studio-glance-hint">{rand(total.ordersWonMinor)} · avg {rand(total.averageOrderMinor)}</span>
              </button>
              <button type="button" onClick={() => onOpenPayments?.('outstanding')}>
                <small>Still owed on {data.year} orders</small>
                <strong>{rand(total.outstandingMinor)}</strong>
                <span className="studio-glance-hint">{total.overdueMinor > 0 ? `${rand(total.overdueMinor)} overdue` : 'nothing overdue'}</span>
              </button>
              <button type="button" onClick={() => onOpenClients?.('delivered')}>
                <small>Shoots</small>
                <strong>{total.shootsDone}</strong>
                <span className="studio-glance-hint">{total.shootsDelivered} delivered{total.averageTurnaroundDays ? ` · ${total.averageTurnaroundDays}-day turnaround` : ''}</span>
              </button>
              <button type="button" onClick={() => onOpenClients?.('quoted')}>
                <small>Conversion</small>
                <strong>{total.conversionRate}%</strong>
                <span className="studio-glance-hint">{total.ordersWon} won · {total.ordersDeclined} declined · {total.quotesIssued} quoted</span>
              </button>
            </div>
            {previous && (previous.ordersWon || previous.netReceivedMinor) ? (
              <p className="studio-note">
                {data.previousYear.year}: {rand(previous.netReceivedMinor)} cash in · {previous.ordersWon} orders · {previous.shootsDone} shoots.
              </p>
            ) : null}
          </section>

          <section aria-label="By month">
            <h3 className="studio-section-label">By month</h3>
            <div className="studio-table-wrap">
              <table className="studio-table studio-finance-table" data-testid="finance-months">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Enquiries</th>
                    <th>Quoted</th>
                    <th>Won</th>
                    <th>Orders</th>
                    <th>Cash in</th>
                    <th>Still owed</th>
                    <th>Shoots</th>
                    <th>Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {shownMonths.length === 0 ? (
                    <tr>
                      <td colSpan={9}>No months with activity yet this year.</td>
                    </tr>
                  ) : null}
                  {shownMonths.map((row) => {
                    const isOpen = current ? row.key === current.key : false;
                    const isNow = row.key === data.thisMonth;
                    return (
                      <tr
                        key={row.key}
                        className={`${isOpen ? 'is-selected' : ''}${isNow ? ' is-now' : ''}`}
                        onClick={() => setOpenMonth(row.key)}
                        style={{ cursor: 'pointer' }}
                        data-testid={`finance-month-${row.key}`}
                      >
                        <td data-label="Month"><strong>{row.label}</strong>{isNow ? <span className="studio-row-sub">this month</span> : null}</td>
                        <td data-label="Enquiries">{row.enquiries || '—'}</td>
                        <td data-label="Quoted">{row.quotesIssued ? `${row.quotesIssued} · ${rand(row.quotesIssuedMinor)}` : '—'}</td>
                        <td data-label="Won">{row.ordersWon ? `${row.ordersWon}${row.ordersDeclined ? ` (${row.conversionRate}%)` : ''}` : (row.ordersDeclined ? `0 (${row.ordersDeclined} declined)` : '—')}</td>
                        <td data-label="Orders">
                          <span className="studio-amount">{row.ordersWonMinor ? rand(row.ordersWonMinor) : '—'}</span>
                          <MonthBar value={row.ordersWonMinor} max={maxWon} tone="won" />
                        </td>
                        <td data-label="Cash in">
                          <span className="studio-amount">{row.netReceivedMinor ? rand(row.netReceivedMinor) : '—'}</span>
                          <MonthBar value={row.netReceivedMinor} max={maxCash} tone="cash" />
                        </td>
                        <td data-label="Still owed" className={row.overdueMinor > 0 ? 'is-overdue' : ''}>
                          {row.outstandingMinor ? rand(row.outstandingMinor) : '—'}
                          {row.overdueMinor > 0 ? <span className="studio-row-sub">{rand(row.overdueMinor)} overdue</span> : null}
                        </td>
                        <td data-label="Shoots">{row.shootsDone || row.shootsConfirmed ? `${row.shootsDone}${row.shootsConfirmed > row.shootsDone ? ` of ${row.shootsConfirmed}` : ''}` : '—'}</td>
                        <td data-label="Delivered">{row.shootsDelivered ? `${row.shootsDelivered}${row.averageTurnaroundDays ? ` · ${row.averageTurnaroundDays}d` : ''}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>{data.year}</strong></td>
                    <td>{total.enquiries}</td>
                    <td>{total.quotesIssued} · {rand(total.quotesIssuedMinor)}</td>
                    <td>{total.ordersWon} ({total.conversionRate}%)</td>
                    <td><span className="studio-amount">{rand(total.ordersWonMinor)}</span></td>
                    <td><span className="studio-amount">{rand(total.netReceivedMinor)}</span></td>
                    <td>{rand(total.outstandingMinor)}</td>
                    <td>{total.shootsDone}</td>
                    <td>{total.shootsDelivered}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="studio-note">
              Orders sit in the month the quotation was accepted. Cash sits in the month it was paid, after refunds.
              "Still owed" is what remains on that month's orders today. Shoots count by shoot date; delivered by hand-over date.
            </p>
          </section>

          {current ? (
            <div className="studio-split">
              <section className="studio-panel" aria-label={`${current.label} ${data.year} in detail`} data-testid="finance-month-detail">
                <h3 className="studio-section-label">{current.label} {data.year}</h3>
                <dl className="studio-summary-list">
                  <div><dt>Enquiries</dt><dd>{current.enquiries}</dd></div>
                  <div><dt>Quotations sent</dt><dd>{current.quotesIssued} · {rand(current.quotesIssuedMinor)}</dd></div>
                  <div><dt>Orders won</dt><dd>{current.ordersWon} · {rand(current.ordersWonMinor)}{current.ordersWon ? ` · avg ${rand(current.averageOrderMinor)}` : ''}</dd></div>
                  <div><dt>Declined</dt><dd>{current.ordersDeclined}</dd></div>
                  <div><dt>Deposits in</dt><dd>{rand(current.depositsMinor)}</dd></div>
                  <div><dt>Balances in</dt><dd>{rand(current.balancesMinor)}</dd></div>
                  {current.refundsMinor ? <div><dt>Refunded</dt><dd>{rand(current.refundsMinor)}</dd></div> : null}
                  <div><dt>Cash in, net</dt><dd><strong>{rand(current.netReceivedMinor)}</strong></dd></div>
                  <div><dt>Still owed on these orders</dt><dd>{rand(current.outstandingMinor)}{current.overdueMinor ? ` · ${rand(current.overdueMinor)} overdue` : ''}</dd></div>
                  <div><dt>Shoots</dt><dd>{current.shootsDone} done · {current.shootsConfirmed} on the calendar</dd></div>
                  <div><dt>Delivered</dt><dd>{current.shootsDelivered}{current.averageTurnaroundDays ? ` · ${current.averageTurnaroundDays} days from shoot to hand-over` : ''}</dd></div>
                </dl>
              </section>
              <section className="studio-panel" aria-label="By shoot type" data-testid="finance-shoot-types">
                <h3 className="studio-section-label">Orders by shoot type · {current.ordersWon ? `${current.label}` : `${data.year}`}</h3>
                {shootTypes.length === 0 ? <p className="studio-empty">No orders won yet.</p> : (
                  <table className="studio-table">
                    <thead><tr><th>Type</th><th>Orders</th><th>Value</th></tr></thead>
                    <tbody>
                      {shootTypes.map((row) => (
                        <tr key={row.type}>
                          <td data-label="Type">{row.type}</td>
                          <td data-label="Orders">{row.orders}</td>
                          <td data-label="Value"><span className="studio-amount">{rand(row.valueMinor)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

export default StudioFinance;
